import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, ViewChild, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AddRequiredPipe } from '@pipe/add-required.pipe';
import { HttpClientService } from '@services/http-client.service';
import { LoggerService } from '@services/logger.service';
import { OtpInputComponent } from '@calio-lib/otp-input/otp-input.component';

/** Seconds the Resend link stays disabled after a code is sent (JB-1686). */
const RESEND_COOLDOWN_SECONDS = 30;

/**
 * Signup Rules P5 -- email confirmation OTP.
 *
 * The email twin of OtpStepComponent (SMS): auto-sends a code to `email` on
 * init, lets the user verify or resend, and emits onNextEvent once verification
 * succeeds so the parent re-submits the registration. Hits the email OTP
 * endpoints keyed on the email address. When both email + mobile verification
 * are on, the backend rejects with email.otpNotVerified first, so this step runs
 * before the SMS step (email-then-mobile ordering falls out of the resubmit).
 *
 * JB-1686 UX: shows a progress cue ("Verification x of y"), auto-focuses and
 * auto-submits the code, offers an "edit email" escape hatch, and gates the
 * Resend link behind a visible cooldown (with a "new code sent" confirmation).
 */
@Component({
  selector: 'app-email-otp-step',
  templateUrl: './email-otp-step.component.html',
  styleUrls: ['./email-otp-step.component.scss'],
  imports: [FormsModule, TranslateModule, AddRequiredPipe, OtpInputComponent],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EmailOtpStepComponent implements OnInit {

  email = input.required<string>();
  // JB-1686: position of this screen in the verification sequence + the total
  // (e.g. 1 of 2 when both email and SMS verification are required). total <= 1
  // renders a single "Last step" cue instead of "x of y".
  verificationStep = input<number>(1);
  verificationTotal = input<number>(1);

  protected onNextEvent = output<void>();
  // JB-1686: "wrong email? edit it" — hand control back to the parent so it can
  // return to the step that owns the email field with the data preserved.
  protected onEditContact = output<void>();

  @ViewChild(OtpInputComponent) private otpInput?: OtpInputComponent;

  private destroyRef = inject(DestroyRef);
  private httpClientService = inject(HttpClientService);
  private translate = inject(TranslateService);

  protected otpCode$ = signal('');
  protected isSending$ = signal(false);
  protected showLoader$ = signal(false);
  protected resendBlocked$ = signal(false);
  // JB-1686: client-side resend cooldown (seconds remaining) + one-shot
  // "new code sent" confirmation after a manual resend.
  protected resendCooldown$ = signal(0);
  protected codeSentConfirm$ = signal(false);
  protected expiresAt$ = signal<string>(undefined);
  protected ttlMinutes$ = signal(10);
  protected errorMessageKey$ = signal<string>(undefined);
  protected blockedTime$ = signal(0);
  // Mirrors the backend send cap (MAX_SENDS_PER_WINDOW) shown in the hint.
  protected readonly maxSendsPerHour = 3;

  private lastAutoSubmitted = '';
  private cooldownTimer?: ReturnType<typeof setInterval>;

  ngOnInit(): void {
    this.sendOtp();
  }

  // JB-1686: submit as soon as a full 6-digit code is entered (once per distinct
  // code, so a rejected code isn't resubmitted until the user changes it).
  protected onCodeInput(value: string): void {
    this.otpCode$.set(value);
    if (value.length === 6 && value !== this.lastAutoSubmitted && !this.showLoader$() && !this.isSending$()) {
      this.lastAutoSubmitted = value;
      this.verifyOtp();
    }
  }

  protected editContact(): void {
    this.onEditContact.emit();
  }

  protected resendOtp(): void {
    if (this.resendBlocked$() || this.resendCooldown$() > 0 || this.isSending$()) {
      return;
    }
    this.otpCode$.set('');
    this.lastAutoSubmitted = '';
    this.otpInput?.clear();
    this.sendOtp(true);
  }

  protected verifyOtp(): void {
    if (this.showLoader$() || this.isSending$() || !this.otpCode$()) {
      return;
    }
    this.showLoader$.set(true);
    this.errorMessageKey$.set(undefined);

    this.httpClientService.post('partners/verify_registration_email_otp', { email: this.email(), otp: this.otpCode$() })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.showLoader$.set(false);
          this.onNextEvent.emit();
        },
        error: (error: HttpErrorResponse) => {
          this.showLoader$.set(false);
          this.handleOtpError(error);
          LoggerService.error(error);
        }
      });
  }

  private sendOtp(isResend = false): void {
    this.isSending$.set(true);
    this.errorMessageKey$.set(undefined);
    this.codeSentConfirm$.set(false);

    // Send the registrant's active language so the OTP email renders in it
    // (the parent sets it via TranslateService.setDefaultLang(widget language)).
    const language = this.translate.currentLang || this.translate.getDefaultLang();
    this.httpClientService.post('partners/send_registration_email_otp', { email: this.email(), language })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: result => {
          this.isSending$.set(false);
          this.expiresAt$.set(result?.expires_at);
          this.ttlMinutes$.set(result?.ttl_minutes ?? 10);
          this.startResendCooldown();
          this.focusCodeInput();
          if (isResend) {
            this.codeSentConfirm$.set(true);
            setTimeout(() => this.codeSentConfirm$.set(false), 4000);
          }
        },
        error: (error: HttpErrorResponse) => {
          this.isSending$.set(false);
          this.handleOtpError(error);
          LoggerService.error(error);
        }
      });
  }

  // JB-1686: keep Resend disabled for RESEND_COOLDOWN_SECONDS after a send, with
  // a live countdown. The link always re-enables (no permanent lock-out).
  private startResendCooldown(): void {
    clearInterval(this.cooldownTimer);
    this.resendCooldown$.set(RESEND_COOLDOWN_SECONDS);
    this.cooldownTimer = setInterval(() => {
      const remaining = this.resendCooldown$() - 1;
      this.resendCooldown$.set(Math.max(0, remaining));
      if (remaining <= 0) {
        clearInterval(this.cooldownTimer);
      }
    }, 1000);
    this.destroyRef.onDestroy(() => clearInterval(this.cooldownTimer));
  }

  private focusCodeInput(): void {
    setTimeout(() => this.otpInput?.focus(), 0);
  }

  private handleOtpError(error: HttpErrorResponse): void {
    // Read the identifier from the flat body shape (top-level, what every
    // controller sends) OR the nested shape some middleware 429s use
    // (`error.error.identifier`), so a real rate-limit isn't misread as generic.
    const identifier: string = error?.error?.identifier ?? error?.error?.error?.identifier;

    if (identifier === 'TOO_MANY_REQUESTS') {
      const retryAfterHeader = error.headers?.get('Retry-After');
      const retryAfter = retryAfterHeader ? +retryAfterHeader : 0;

      if (retryAfter > 0) {
        this.resendBlocked$.set(true);
        if (retryAfter > 60) {
          this.blockedTime$.set(Math.floor(retryAfter / 60));
          this.errorMessageKey$.set(this.blockedTime$() === 1 ? 'exceptions.blockforminute' : 'exceptions.blockforminutes');
        } else {
          this.blockedTime$.set(retryAfter);
          this.errorMessageKey$.set(retryAfter === 1 ? 'exceptions.blockforsecond' : 'exceptions.blockforseconds');
        }
        setTimeout(() => this.resendBlocked$.set(false), retryAfter * 1000);
      } else {
        this.errorMessageKey$.set('exceptions.toomanyrequestsexception');
      }
      return;
    }

    this.errorMessageKey$.set(this.mapOtpErrorIdentifier(identifier));
  }

  private mapOtpErrorIdentifier(identifier: string): string {
    switch (identifier) {
      case 'EMAIL_MISSING':
        return 'otp.email_missing';
      case 'OTP_MISSING':
        return 'otp.otp_missing';
      case 'EMAIL_SEND_FAILED':
        return 'otp.email_send_failed';
      case 'OTP_NOT_FOUND':
        return 'otp.otp_not_found';
      case 'OTP_EXPIRED':
        return 'otp.otp_expired';
      case 'INVALID_OTP':
        return 'otp.invalid_otp';
      case 'REACHED_MAX_LIMIT':
        return 'otp.reached_max_limit';
      default:
        return 'otp.generic';
    }
  }
}
